import { useRef, useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useProfilePictureGeneric } from "@/lib/hooks/use-profile-picture-generic";

type ProfileType = 'freelancer' | 'client';

interface ProfilePictureUploadGenericProps {
  profileType: ProfileType;
  profileId?: string | null;
  currentUrl?: string | null;
  name?: string | null;
  className?: string;
  onImageUploaded?: (imageUrl: string) => void;
}

export function ProfilePictureUploadGeneric({ 
  profileType, 
  profileId, 
  currentUrl, 
  name,
  className = "",
  onImageUploaded
}: ProfilePictureUploadGenericProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadProfilePicture, deleteProfilePicture, isUploading, isDeleting, uploadedImageUrl, isUploadSuccess } = useProfilePictureGeneric({
    profileType,
    profileId,
  });

  // State to track the displayed image URL for smoother transitions
  const [displayedImageUrl, setDisplayedImageUrl] = useState(currentUrl);

  // Update displayed image when current URL changes
  useEffect(() => {
    if (currentUrl !== displayedImageUrl) {
      // If it's a blob URL (optimistic), show it immediately
      if (currentUrl?.startsWith('blob:')) {
        setDisplayedImageUrl(currentUrl);
      } else if (currentUrl) {
        // If it's a real URL, preload it first for smooth transition
        const img = new Image();
        img.onload = () => setDisplayedImageUrl(currentUrl);
        img.onerror = () => setDisplayedImageUrl(currentUrl); // Show anyway if loading fails
        img.src = currentUrl;
      } else {
        // No image
        setDisplayedImageUrl(null);
      }
    }
  }, [currentUrl, displayedImageUrl]);

  // Handle successful image upload during onboarding (when no profile exists yet)
  useEffect(() => {
    if (isUploadSuccess && uploadedImageUrl && onImageUploaded && !profileId) {
      onImageUploaded(uploadedImageUrl);
      // For onboarding, show the uploaded image immediately
      setDisplayedImageUrl(uploadedImageUrl);
    }
  }, [isUploadSuccess, uploadedImageUrl, onImageUploaded, profileId]);

  const initials = name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase() || "?";

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    uploadProfilePicture(file);
    
    // Clear the input to allow selecting the same file again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = () => {
    // Use the current URL (not the displayed one which might be a blob)
    if (currentUrl && !currentUrl.startsWith('blob:')) {
      deleteProfilePicture(currentUrl);
    }
  };

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <Avatar className="h-20 w-20">
        <AvatarImage 
          src={displayedImageUrl || undefined} 
          alt={name || "Profile"} 
        />
        <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
      </Avatar>

      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleUploadClick}
          disabled={isDeleting}
        >
          <Camera className="h-4 w-4 mr-2" />
          {displayedImageUrl ? 'Change Photo' : 'Upload Photo'}
        </Button>

        {displayedImageUrl && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDelete}
            disabled={isUploading}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Remove Photo
          </Button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}